// Shared briefing-scoped data fetchers used by both the mobile tab
// queries (useQuery in briefing/index/schedule/lineup/tracking) AND
// the pre-navigation prefetch in production-selector. Having both
// sides import from here prevents the shape drift that repeatedly
// caused "data missing on first render, appears after pull-to-
// refresh" regressions when someone updated one side and forgot the
// other.
//
// Each fetcher returns data in the exact shape useQuery expects,
// including any secondary fetches / joins / post-processing. Do not
// inline Supabase queries in the component code for these keys —
// call the fetcher instead.

// deno-lint-ignore no-explicit-any
type Client = any; // kept loose so both web and mobile Supabase clients work

// ───────────── briefing (basic row, used by home/schedule/lineup/tracking)

export async function fetchBriefing(client: Client, briefingId: string) {
  const { data, error } = await client.from("briefings").select("*").eq("id", briefingId).single();
  if (error) throw error;
  return data;
}

// ───────────── briefing-detail (briefing + producer profile, briefing tab)

interface ProducerProfile {
  full_name: string | null;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  invoice_information: unknown;
}

export async function fetchBriefingDetail(
  client: Client,
  briefingId: string,
  options?: { optionalProducer?: boolean },
) {
  const { data, error } = await client.from("briefings").select("*").eq("id", briefingId).single();
  if (error) throw error;
  if (data?.producer_id) {
    const { data: producerData, error: producerError } = await client
      .from("profiles")
      .select("full_name, email, phone, company_name, invoice_information")
      .eq("id", data.producer_id)
      .single();
    if (producerError) {
      // Default (mobile briefing tab): throw so the cached briefing +
      // producer info stays visible on a refetch hiccup; returning
      // producer: null would flash "Unknown producer".
      //
      // optionalProducer (web read-only portal view): the viewer may be
      // a co-producer who is RLS-blocked from the owner's profile. The
      // briefing must still load — just drop the producer card rather
      // than failing the whole page.
      if (!options?.optionalProducer) throw producerError;
      return { ...data, producer: null };
    }
    return { ...data, producer: producerData as ProducerProfile | null };
  }
  return data;
}

// ───────────── crew member ids (for current user on this briefing)

export async function fetchCrewMemberIds(
  client: Client,
  briefingId: string,
  userId: string,
): Promise<string[]> {
  const { data, error } = await client
    .from("crew_members")
    .select("id")
    .eq("user_id", userId)
    .eq("briefing_id", briefingId);
  if (error) throw error;
  return (data ?? []).map((row: { id: string }) => row.id);
}

// ───────────── crew members list (all crew on this briefing, sorted by name)

export async function fetchCrewMembers(client: Client, briefingId: string) {
  const { data, error } = await client
    .from("crew_members")
    .select("*")
    .eq("briefing_id", briefingId)
    .order("name");
  if (error) throw error;
  return data ?? [];
}

// ───────────── lineup (all dates, sorted by set start)

export async function fetchLineup(client: Client, briefingId: string) {
  const { data, error } = await client
    .from("lineup")
    .select("*")
    .eq("briefing_id", briefingId)
    .order("set_start");
  if (error) throw error;
  return data ?? [];
}

// ───────────── crew assignments (all dates, current user only, active only)
//
// Both steps MUST throw on supabase error (not silently fall through to
// []) because useQuery on schedule.tsx runs with refetchOnMount:
// "always" — a transient RLS / auth-token / network failure that
// returned [] would overwrite the real assignments already in the
// cache and the schedule tab would flash empty. Throwing keeps
// react-query in an error state with `data` still populated from the
// last successful fetch, matching the offline-first expectation.

export async function fetchCrewAssignments(client: Client, briefingId: string, userId: string) {
  const { data: crewMembers, error: membersError } = await client
    .from("crew_members")
    .select("id")
    .eq("user_id", userId)
    .eq("briefing_id", briefingId);
  if (membersError) throw membersError;
  if (!crewMembers || crewMembers.length === 0) return [];
  const ids = crewMembers.map((cm: { id: string }) => cm.id);
  const { data, error } = await client
    .from("crew_assignments")
    .select("*, crew_members(*)")
    .eq("briefing_id", briefingId)
    .in("crew_member_id", ids)
    .eq("is_active", true)
    .order("filming_start");
  if (error) throw error;
  return data ?? [];
}

// ───────────── all crew assignments for the briefing (every member, every date)
//
// Used to power the "view any crew member's schedule" drill-down from the
// crew-list screen. Fetching per-member would mean N extra round-trips
// whenever someone opens crew-list; instead we pull every active assignment
// once and let the consumer filter client-side by crew_member_id + date.

export async function fetchAllCrewAssignments(client: Client, briefingId: string) {
  const { data, error } = await client
    .from("crew_assignments")
    .select("*")
    .eq("briefing_id", briefingId)
    .eq("is_active", true)
    .order("filming_start");
  if (error) throw error;
  return data ?? [];
}

// ───────────── crew completed assignments (per user, global)

export async function fetchCrewCompletedAssignmentIds(
  client: Client,
  userId: string,
): Promise<string[]> {
  // Throw on error (don't silently return []) for the same reason as
  // fetchCrewAssignments: schedule.tsx uses refetchOnMount "always",
  // and a transient failure that returned [] would wipe the checked-
  // off state so every task flips back to "not done" until the next
  // successful refetch.
  const { data, error } = await client
    .from("crew_completed_assignments")
    .select("assignment_id")
    .eq("user_id", userId);
  if (error) throw error;
  return (data ?? []).map((row: { assignment_id: string }) => row.assignment_id);
}

// ───────────── crew member arrival (personal call time + role for user)
//
// Returns ALL of the user's crew_members rows for the briefing —
// `crew_members` is per-day (one row per (briefing, person, event_date))
// and the producer can set a different role and arrival time on each
// day. Sorted by event_date ascending so the briefing tab can render
// shifts in chronological order.

export type CrewMemberShift = {
  event_date: string;
  arrival_time: string | null;
  function: string;
  name: string;
  camera_id: string | null;
};

export async function fetchCrewMemberArrival(
  client: Client,
  briefingId: string,
  userId: string,
): Promise<CrewMemberShift[]> {
  // Throw on error so the already-cached arrival_time + role survive
  // a transient failure; otherwise the briefing tab would flash back
  // to "no role / no call time" every time a refetch hiccupped.
  const { data, error } = await client
    .from("crew_members")
    .select("event_date, arrival_time, name, function, camera_id")
    .eq("briefing_id", briefingId)
    .eq("user_id", userId)
    .order("event_date", { ascending: true });
  if (error) throw error;
  return (data ?? []) as CrewMemberShift[];
}

// ───────────── producer subscription (rpc — for tracking tab)

export async function fetchProducerSubscription(client: Client, briefingId: string) {
  const { data, error } = await client.rpc("get_briefing_producer_subscription", {
    p_briefing_id: briefingId,
  });
  if (error) throw error;
  return data?.[0] ?? null;
}

// ───────────── briefing weather (cached forecast for the weather card)
//
// Reads the single cached row written server-side by the refresh-weather
// edge function. Returns null when no forecast has been computed yet (e.g.
// a brand-new briefing before the first cron sweep) so the card can show a
// neutral "forecast on the way" state rather than an error.

import type { BriefingWeather } from "./weatherDisplay";

export async function fetchBriefingWeather(
  client: Client,
  briefingId: string,
): Promise<BriefingWeather | null> {
  const { data, error } = await client
    .from("briefing_weather")
    .select("briefing_id, latitude, longitude, coord_source, daily, hourly, summary, fetched_at")
    .eq("briefing_id", briefingId)
    .maybeSingle();
  if (error) throw error;
  return (data as BriefingWeather | null) ?? null;
}

// ───────────── per-day doors overrides
//
// One row per day that overrides the briefing's own doors window. Feed the
// result to doorsForDay(); an empty list simply means every day uses the
// briefing-level fallback.

import type { DayDoors } from "./doorsForDay";

export async function fetchDayDoors(client: Client, briefingId: string): Promise<DayDoors[]> {
  const { data, error } = await client
    .from("briefing_day_doors")
    .select("event_date, doors_open, doors_close")
    .eq("briefing_id", briefingId);
  if (error) throw error;
  return (data as DayDoors[] | null) ?? [];
}

// ───────────── festival areas (stage zones + media office + crew catering)

export async function fetchFestivalAreas(
  client: Client,
  briefingId: string,
  options?: {
    /**
     * Also read the festival_boundary polygon (after the zones). The crew map
     * leaves the outer edge off; the producer app's map draws it as the dashed
     * frame the producer web maps show.
     */
    includeBoundary?: boolean;
  },
) {
  // Freshness is driven by `festival-areas-changed` silent-push invalidation
  // plus refetchOnMount:"always" on the mobile tracking query — NOT by
  // polling. Throw on error → react-query keeps the previously-cached areas
  // (so a transient failure never wipes the polygons drawn on the map)
  // until the next successful refetch.
  const { data: stageZones, error: stageError } = await client
    .from("festival_areas")
    .select("*")
    .eq("briefing_id", briefingId)
    .eq("area_type", "stage_zone");
  if (stageError) throw stageError;
  const locationTypes = options?.includeBoundary
    ? ["media_office", "crew_catering", "festival_boundary"]
    : ["media_office", "crew_catering"];
  const { data: locationZones, error: locationError } = await client
    .from("festival_areas")
    .select("*")
    .eq("briefing_id", briefingId)
    .in("area_type", locationTypes);
  if (locationError) throw locationError;
  return [...(stageZones ?? []), ...(locationZones ?? [])];
}
